import { Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { EMPTY, forkJoin, Observable, tap } from 'rxjs';
import { DataResult, GroupDescriptor, process, SortDescriptor } from '@progress/kendo-data-query';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { TabStripComponent, TabStripModule } from '@progress/kendo-angular-layout';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faDownload, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { ProfileRoleConstants } from 'src/app/common/ProfileRole';
import {
  NeutralLanguage,
  ResolutionSubjectConstants,
  ResolutionVocabularyEntry,
  ResolutionVocabularyExportFileName,
} from 'src/app/common/ResolutionVocabularyEntry';
import { ProductCategory, productCategoryLabel } from 'src/app/common/ProductCategory';
import { vatLabel } from 'src/app/common/vat-label';
import { Vat } from 'src/app/common/Vat';
import { AppProfileService } from 'src/app/services/profile.service';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { ResolutionVocabularyService } from 'src/app/services/resolution-vocabulary.service';
import { VatService } from 'src/app/services/vat.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import {
  ResolutionObservationsComponent,
  VocabularyPrefill,
} from '../resolution-observations/resolution-observations.component';
import { VatAiAcceptanceComponent } from '../vat-ai-acceptance/vat-ai-acceptance.component';
import { saveBlob } from 'src/app/common/browser-files';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

interface SelectItem {
  value: string;
  label: string;
}

// Grid row: entry enriched with the translated display labels the grid groups/sorts on.
interface VocabularyRow extends ResolutionVocabularyEntry {
  subjectLabel: string;
  // Short form for the narrow column; the long form („Neutral (alle Sprachen)") is its tooltip.
  languageLabel: string;
  languageTitle: string;
  targetLabel: string;
  originLabel: string;
  // #623 sign-off, pre-computed for the grid: only VAT rows carry one (the server 400s a
  // verify on category rows), so only they get the „Geprüft" cell and the verify button.
  needsSignOff: boolean;
  verifiedAtDate: Date | null;
  // The acting subject is a raw `sub` — tooltip material, never a column value.
  verifiedByTitle: string;
  // Backend #629 follow-up: birth provenance as the Herkunft badge's tooltip — where the
  // term was first observed ('' when none: shipped dictionary / freehand entry).
  sourceTitle: string;
}

// Backend #617 B3: the curation surface over the resolution vocabulary — the loop-closer of
// the resolution chain: a miss observed in the shadow data becomes a term here, and it fires
// in the very next scan (no cache, no deploy). One page: add/edit form above, grid below.
// Carrier-admin only (the vocabulary is HOUSE-WIDE by decision — #538/#617); the BFF
// enforces 403 server-side, the menu link mirrors it client-side.
// #623 adds the sign-off surface on top: MwSt. rows carry a „Geprüft" state and a verify
// action — the tax-advisor review #579 decision 4 makes the precondition for Live VAT.
/** The add/edit form of one recognition term. Its type is inferred from here (#199). */
function buildEntryForm(fb: FormBuilder) {
  return fb.group({
    // Typed explicitly: the constant would narrow `subject` to one literal, a bare `null` the
    // others to `null` (#199).
    subject: fb.control<string>(ResolutionSubjectConstants.ProductCategory, Validators.required),
    term: fb.control<string>(null, [Validators.required, Validators.maxLength(200)]),
    language: fb.control<string>(null, Validators.required),
    target: fb.control<string>(null, Validators.required),
  });
}
type EntryForm = ReturnType<typeof buildEntryForm>;

@Component({
  imports: [
    CommonModule,
    KENDO_GRID,
    KENDO_DROPDOWNS,
    TabStripModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    AsyncClickDirective,
    ResolutionObservationsComponent,
    VatAiAcceptanceComponent,
    GridNoRecordsComponent,
  ],
  selector: 'app-resolution-vocabulary',
  templateUrl: './resolution-vocabulary.component.html',
  styleUrls: ['./resolution-vocabulary.component.css'],
})
export class ResolutionVocabularyComponent implements OnInit {
  fb = inject(FormBuilder);
  private vocabularyService = inject(ResolutionVocabularyService);
  private productCategoryService = inject(ProductCategoryService);
  private vatService = inject(VatService);
  private profileService = inject(AppProfileService);
  private apiActionService = inject(CallApiService);
  private datePipe = inject(DatePipe);

  private destroyRef = inject(DestroyRef);

  public faEdit = faEdit;
  public faTrashAlt = faTrashAlt;
  public faCheck = faCheck;
  public faDownload = faDownload;

  public profile: ExtendedProfile;
  public isAdmin = false;

  public entryForm: EntryForm;
  // null = creating; an id = editing that row (subject immutable server-side, so the
  // subject picker is disabled while editing).
  public editingId: string | null = null;

  // Backend #629 follow-up: the provenance snapshot the last observation takeover carried
  // ("<binding rootUrl> (<customer name>)"), together with the term it belongs to. Sent
  // with the create only while the term is still the observed one — a curator who rewrites
  // the term into something else is no longer recording an observation, and the claim must
  // not survive that. Cleared with every form reset.
  private pendingSource: string | null = null;
  private pendingSourceTerm: string | null = null;

  public readonly subjectItems: SelectItem[] = [
    { value: ResolutionSubjectConstants.ProductCategory, label: $localize`Artikelkategorie` },
    { value: ResolutionSubjectConstants.Vat, label: $localize`MwSt.-Satz` },
  ];

  // The language groups the resolution vocabulary carries (backend #617): a term is either
  // language-specific or neutral (fires under every content language).
  public readonly languageItems: SelectItem[] = [
    { value: 'de', label: $localize`Deutsch` },
    { value: 'en', label: $localize`Englisch` },
    { value: NeutralLanguage, label: $localize`Neutral (alle Sprachen)` },
  ];

  // The grid says „Neutral" and keeps the long form as the cell's tooltip: the column is
  // narrow, and the paragraph above the grid already explains what neutral means. Every
  // pixel saved here goes to „Begriff", the column that actually carries the content.
  private readonly languageShortLabels: Record<string, string> = {
    de: $localize`Deutsch`,
    en: $localize`Englisch`,
    [NeutralLanguage]: $localize`Neutral`,
  };

  public gridView: DataResult;
  public groups: GroupDescriptor[] = [{ field: 'subjectLabel', dir: 'asc' }];
  public sort: SortDescriptor[] = [];

  readonly tabstrip = viewChild(TabStripComponent);

  private rows: VocabularyRow[] = [];
  // Public: the observation tab (#623) reuses them to label resolved/tied target ids.
  public categories: ProductCategory[] = [];
  public vats: Vat[] = [];

  get f() {
    return this.entryForm?.controls;
  }

  // Targets are logical keys per subject: ProductCategory shortNames, or the seeded VAT
  // percentages — only what actually exists is offered (the backend 400s anything else).
  // #178: the VALUE stays the key, because that is what the backend stores and validates; only
  // the LABEL becomes the resolved name. Sorted by the label, so the list reads in the order the
  // Carrier sees rather than in ASCII-key order.
  public get targetItems(): SelectItem[] {
    return this.entryForm?.controls.subject?.value === ResolutionSubjectConstants.Vat
      ? this.vats.map((v) => ({ value: `${v.percentage}`, label: vatLabel(v) }))
      : this.categories
          .map((c) => ({ value: c.shortName, label: productCategoryLabel(c) }))
          .sort((a, b) => a.label.localeCompare(b.label));
  }

  public ngOnInit() {
    this.entryForm = this.createForm();
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.isAdmin = p?.role == ProfileRoleConstants.RoleIdAdmin;
      this.load();
    });
  }

  public onSortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    this.refreshGrid();
  }

  public onSubjectChange() {
    // The target is subject-scoped — a category shortName means nothing to the VAT subject.
    this.entryForm.controls.target.setValue(null);
  }

  public onEdit(row: VocabularyRow) {
    this.editingId = row.id;
    this.entryForm.setValue({
      subject: row.subject,
      term: row.term,
      language: row.language,
      target: row.target,
    });
    this.entryForm.controls.subject.disable();
    this.entryForm.markAsPristine();
  }

  public onCancelEdit() {
    this.editingId = null;
    this.pendingSource = null;
    this.pendingSourceTerm = null;
    this.entryForm.controls.subject.enable();
    this.entryForm.reset({ subject: ResolutionSubjectConstants.ProductCategory });
  }

  // #623: a chip clicked on the observation tab — the miss becomes a pre-filled add form:
  // term in „Begriff", subject and language pre-selected from the observation, target left
  // for the human decision. Curation as review instead of transcription.
  public onPrefillFromObservation(prefill: VocabularyPrefill) {
    if (this.editingId) {
      this.onCancelEdit();
    }
    this.entryForm.patchValue({
      subject: prefill.subject,
      term: prefill.term,
      language: prefill.language,
      target: null,
    });
    // #629 follow-up: remember where the term was observed — stamped onto the create as its
    // birth provenance (set AFTER the cancel above, which clears any earlier pending source).
    this.pendingSource = prefill.source;
    this.pendingSourceTerm = prefill.source ? prefill.term : null;
    this.entryForm.markAsDirty();
    this.tabstrip()?.selectTab(0);
  }

  public submitForm(): Observable<unknown> {
    if (!this.entryForm.valid) {
      return EMPTY;
    }

    const value = this.entryForm.getRawValue();
    const action$ = this.editingId
      ? this.apiActionService.callWithToasts$(
          this.vocabularyService.update(this.editingId, {
            term: value.term,
            language: value.language,
            target: value.target,
          }),
          $localize`Erkennungsbegriff wurde aktualisiert.`,
          $localize`Beim Aktualisieren des Erkennungsbegriffs ist ein Fehler aufgetreten.`
        )
      : this.apiActionService.callWithToasts$(
          this.vocabularyService.create({
            subject: value.subject,
            term: value.term,
            language: value.language,
            target: value.target,
            source: this.effectiveSource(value.term),
          }),
          $localize`Erkennungsbegriff wurde angelegt.`,
          $localize`Beim Anlegen des Erkennungsbegriffs ist ein Fehler aufgetreten.`
        );

    return action$.pipe(
      tap(() => {
        this.onCancelEdit();
        this.reloadEntries();
      })
    );
  }

  // Backend #629: downloads the canonical vocabulary JSON — the export half of the curation
  // round-trip. The blob is saved EXACTLY as served (same trick as the invoice download):
  // the file is committed into the backend repo verbatim, replacing
  // Catalog.TestData/resolution-vocabulary.json, and the git diff is then precisely the
  // curation delta. No success toast — the browser's own download UI is the feedback.
  public onExport(): Observable<unknown> {
    return this.apiActionService
      .callWithToasts$(
        this.vocabularyService.export(),
        null,
        $localize`Beim Exportieren der Erkennungsbegriffe ist ein Fehler aufgetreten.`
      )
      .pipe(tap((blob) => saveBlob(blob, ResolutionVocabularyExportFileName)));
  }

  // #629 follow-up: the pending observation source, but only while the term is still the
  // observed one (trim/case-lenient — a tidied "T-Shirts " is the same observation, a
  // rewritten word is not). Undefined otherwise, so freehand creates carry no source key.
  private effectiveSource(term: string): string | undefined {
    return this.pendingSource &&
      this.pendingSourceTerm &&
      term?.trim().toLocaleLowerCase() === this.pendingSourceTerm.trim().toLocaleLowerCase()
      ? this.pendingSource
      : undefined;
  }

  // #623: the tax-advisor sign-off — a deliberate act, so it asks first (like delete) and
  // says out loud that a later edit revokes it. The stamp itself is server-owned (the acting
  // human's subject, the server clock), so the grid is reloaded rather than patched locally.
  public onVerify(row: VocabularyRow): () => Observable<unknown> {
    return () =>
      this.apiActionService
        .confirmAndCallWithToasts$(
          this.vocabularyService.verify(row.id),
          $localize`Soll „${row.term}“ → ${row.targetLabel} als steuerlich geprüft freigegeben werden? Jede spätere Änderung des Eintrags hebt die Freigabe wieder auf.`,
          $localize`Eintrag wurde als geprüft freigegeben.`,
          $localize`Bei der Freigabe des Erkennungsbegriffs ist ein Fehler aufgetreten.`
        )
        .pipe(tap(() => this.reloadEntries()));
  }

  public onDelete(row: VocabularyRow): () => Observable<unknown> {
    return () =>
      this.apiActionService
        .confirmAndCallWithToasts$(
          this.vocabularyService.delete(row.id),
          $localize`Soll der Erkennungsbegriff „${row.term}“ wirklich gelöscht werden?`,
          $localize`Erkennungsbegriff wurde gelöscht.`,
          $localize`Beim Löschen des Erkennungsbegriffs ist ein Fehler aufgetreten.`
        )
        .pipe(
          tap(() => {
            if (this.editingId === row.id) {
              this.onCancelEdit();
            }
            this.rows = this.rows.filter((r) => r.id !== row.id);
            this.refreshGrid();
          })
        );
  }

  private load() {
    if (!this.profile?.isCarrier || !this.isAdmin) {
      return;
    }
    this.apiActionService.callWithToasts(
      forkJoin({
        entries: this.vocabularyService.get(),
        categories: this.productCategoryService.get(),
        vats: this.vatService.get(),
      }),
      null,
      $localize`Beim Laden der Erkennungsbegriffe ist ein Fehler aufgetreten.`,
      ({ entries, categories, vats }) => {
        this.categories = categories;
        this.vats = vats;
        this.rows = entries.map((e) => this.toRow(e));
        this.refreshGrid();
      }
    );
  }

  private reloadEntries() {
    this.apiActionService.callWithToasts(
      this.vocabularyService.get(),
      null,
      $localize`Beim Laden der Erkennungsbegriffe ist ein Fehler aufgetreten.`,
      (entries) => {
        this.rows = entries.map((e) => this.toRow(e));
        this.refreshGrid();
      }
    );
  }

  private toRow(entry: ResolutionVocabularyEntry): VocabularyRow {
    return {
      ...entry,
      subjectLabel: this.subjectItems.find((s) => s.value === entry.subject)?.label ?? entry.subject,
      languageLabel: this.languageShortLabels[entry.language] ?? entry.language,
      languageTitle:
        this.languageItems.find((l) => l.value === entry.language)?.label ?? entry.language,
      targetLabel:
        entry.subject === ResolutionSubjectConstants.Vat ? `${entry.target} %` : entry.target,
      // Origin is server-owned: Seeded rows came with the system, Curated rows were born or
      // last edited here (and a future reconciling seeder will never overwrite them).
      originLabel:
        entry.origin === 'Seeded' ? $localize`System` : $localize`Manuell gepflegt`,
      // #623: sign-off is a VAT-only concept — category aliases are taxonomy decisions and
      // need no tax-advisor review (the backend rejects a verify on them).
      needsSignOff: entry.subject === ResolutionSubjectConstants.Vat,
      verifiedAtDate: entry.verifiedAt ? new Date(entry.verifiedAt) : null,
      verifiedByTitle: entry.verifiedBy
        ? $localize`Freigegeben von ${entry.verifiedBy} am ${this.datePipe.transform(entry.verifiedAt, 'short')}`
        : '',
      // #629 follow-up: provenance tooltip — works for Curated rows (born here from an
      // observation) and for Seeded ones alike (a sourced entry that travelled in via the
      // export file keeps saying where it was first observed).
      sourceTitle: entry.source ? $localize`Beobachtet bei: ${entry.source}` : '',
    };
  }

  private createForm(): EntryForm {
    return buildEntryForm(this.fb);
  }

  private refreshGrid() {
    this.gridView = process(this.rows, { group: this.groups, sort: this.sort });
  }
}
